import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

export interface TagItem {
  id: number;      // Unique identifier
  value: string;     // Tag text/label
  tagId: number;   // Tag category/type identifier
}

@Component({
  selector: 'vf-input-tags',
  templateUrl: './input-tags.component.html'
})
export class InputTagsComponent implements OnInit, OnChanges {

  @Input() isMobile: boolean = false;
  @Input() initialTags: TagItem[] = [];
  @Input() suggestedTags: TagItem[] = [];
  @Input() placeholder: string = 'Add tag';
  @Output() tagsChange = new EventEmitter<TagItem[]>();
  @Output() add = new EventEmitter<TagItem>();
  @Output() edit = new EventEmitter<TagItem>();
  @Output() remove = new EventEmitter<TagItem>();

  @ViewChild('inputElement') inputElement!: ElementRef;

  tags: TagItem[] = [];
  inputValue: string = '';
  isDropdownOpen: boolean = false;
  isInputVisible: boolean = true;
  filteredSuggestions: TagItem[] = [];
  editingIndex: number = -1;

  constructor() { }

  ngOnInit(): void {
    this.tags = [...this.initialTags];
    this.filteredSuggestions = [...this.suggestedTags];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialTags) {
      this.tags = [...this.initialTags];
    }
    if (changes.suggestedTags) {
      this.filteredSuggestions = [...this.suggestedTags];
    }
    this.filterSuggestedTags();
  }

  filterSuggestedTags(): void {
    this.filteredSuggestions = this.suggestedTags.filter(
      tagItem => !this.tags.some(tag => tag.id === tagItem.id)
    );
  }

  handleInputChange(event: any): void {
    const value = event.target.value;
    this.inputValue = value;

    if (value.trim() === '') {
      this.filteredSuggestions = this.suggestedTags;
    } else {
      setTimeout(() => {
        this.filteredSuggestions = this.suggestedTags.filter(
          tagItem => tagItem.value.toLowerCase().includes(value.toLowerCase())
        );  
      }, 50);
    }

    this.isDropdownOpen = true;
  }

  handleInputKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.inputValue.trim() !== '') {
      event.preventDefault();
      this.addTag(this.inputValue);
    } else if (event.key === 'Backspace' && this.inputValue === '' && this.tags.length > 0 && this.editingIndex === -1) {
      // Start editing the last tag when backspace is pressed with empty input
      this.editTag(this.tags.length - 1);
    } else if (event.key === 'Escape') {
      this.isDropdownOpen = false;
    }
  }

  showInput(): void {
    this.isInputVisible = true;
    this.isDropdownOpen = true;
    this.filterSuggestedTags();
    setTimeout(() => {
      this.inputElement.nativeElement.focus();
    }, 0);
  }

  addTag(tagText: string): void {
    const trimmedTag = tagText.trim();
    if (trimmedTag === '') return;

    let newTags = [...this.tags];

    if (this.editingIndex >= 0) {
      // Update existing tag
      newTags[this.editingIndex] = {
        ...newTags[this.editingIndex],
        value: trimmedTag
      };
      this.edit.emit(newTags[this.editingIndex]);
      this.editingIndex = -1;
    } else {
      // Check if tag text already exists
      const existingTagIndex = this.tags.findIndex(t => t.value.toLowerCase() === trimmedTag.toLowerCase());

      if (existingTagIndex === -1) {
        // Find if the tag exists in suggestions
        const suggestedTag = this.suggestedTags.find(t =>
          t.value && t.value.toLowerCase() === trimmedTag.toLowerCase()
        );

        if (suggestedTag) {
          // Add the suggested tag (with its ID and tagId)
          newTags.push({ ...suggestedTag });
          this.add.emit(suggestedTag);
        } else {
          // Create a new tag object
          newTags.push({
            id: 0,
            value: trimmedTag,
            tagId: 0 // Default tagId, adjust as needed
          });
          this.add.emit(
            {
              id: 0,
              value: trimmedTag,
              tagId: 0
            }
          );
        }
      }
    }

    this.tags = newTags;
    this.inputValue = '';
    this.isDropdownOpen = false;
    this.tagsChange.emit(this.tags);
  }

  removeTag(indexToRemove: number, event: MouseEvent): void {
    event.stopPropagation(); // Prevent the click from triggering tag edit
    this.remove.emit(this.tags[indexToRemove]);
    this.tags = this.tags.filter((_, index) => index !== indexToRemove);
    this.tagsChange.emit(this.tags);
  }

  editTag(index: number): void {
    this.editingIndex = index;
    this.inputValue = this.tags[index].value;
    this.isInputVisible = true;
    this.isDropdownOpen = true;
    this.filterSuggestedTags();
    setTimeout(() => {
      this.inputElement.nativeElement.focus();
    }, 0);
  }

  selectSuggestion(suggestion: TagItem): void {
    // Check if already exists
    const existingTagIndex = this.tags.findIndex(t => t.id === suggestion.id);

    if (existingTagIndex === -1) {
      this.tags = [...this.tags, { ...suggestion }];
      this.tagsChange.emit(this.tags);
      this.add.emit(suggestion);
    }

    this.inputValue = '';
    this.isDropdownOpen = false;
    this.isInputVisible = true;
  }

  handleInputBlur(): void {
    if (this.inputValue.trim() !== '') {
      this.addTag(this.inputValue);
    }

    this.isInputVisible = true;
    this.isDropdownOpen = false;
  }

  handleInputFocus(): void {
    this.isDropdownOpen = true;
    if (this.inputValue.trim() === '') {
      this.filterSuggestedTags();
    }
  }

  // Method to close dropdown when clicking outside
  onClickOutside(event: Event): void {
    this.isDropdownOpen = false;

    // Auto-save tag on blur if there's input value
    if (this.inputValue.trim() !== '') {
      this.addTag(this.inputValue);
    }

    // Hide input field on blur
    this.isInputVisible = true;
  }

}
